/**
 * Capability matrix — SSOT duy nhất cho mọi quyết định bật/tắt tính năng.
 *
 * Vì sao cần (PLAN_FRONTEND_PROPLUS_TRACKA_2026-09-28 §2.1):
 * - Trước đây có 5 nơi hardcode ngưỡng "pro" rải rác + 3 bản copy `requiresAds`
 *   ⇒ thêm một capability phải sửa N nơi, dễ sót (mất doanh thu hoặc lộ Pro miễn phí).
 * - `dockAccess.ts` dùng `tier === "pro"` ⇒ `enterprise`/`custom`/`internal` bị chặn.
 *
 * `import type` chỉ bị erase lúc build ⇒ không tạo vòng import runtime với `patTier.ts`.
 */
export const TIER_LADDER = [
    "none",
    "free",
    "starter",
    "pro",
    "enterprise",
    "custom",
    "internal",
];
const TIER_INDEX = {
    none: 0,
    free: 1,
    starter: 2,
    pro: 3,
    enterprise: 4,
    custom: 5,
    internal: 6,
};
/**
 * Ngưỡng tối thiểu cho từng capability.
 *
 * Quy ước phân gói (đã chốt 2026-09-28):
 * - `free` = Community — phải dùng được thật (AGENTS.md Rule 15).
 * - `pro` = Pro+ Track A — 100% client-side, KHÔNG phụ thuộc backend.
 * - `enterprise` = Track B — cần backend (EP-0…EP-10). Mặc định TẮT cho tới khi EP tới.
 *
 * Hàng `enterprise` dù đã có code ở client vẫn bị đặt `enterprise`: chúng cần **backend route
 * đã được mount** (EP-5) hoặc **contract đã ổn định** (EP-4) nên bật sớm sẽ hiển thị dữ liệu
 * không đáng tin. Điều chỉnh hàng này khi EP tới — KHÔNG thêm entry mới, chỉ hạ ngưỡng xuống.
 *
 * Sửa 2026-10-08: docblock cũ ở đây liệt kê `scale_sync` là `enterprise`, nhưng nó là `pro`
 * và nay đã là `none`. Ba tên còn lại (`alert_drawing`, `portfolio_analytics`, `whale_campaign`)
 * cũng đúng vị trí: `alert_drawing` là `pro`, hai tên kia là `enterprise`. Docblock cũ không
 * khớp ma trận ngay từ lúc viết — đã kiểm từng dòng thay vì tin lời mô tả.
 */
const MIN_TIER = {
    // ---- Community: minTier "none" ⇒ mở kể cả khi CHƯA xác minh tier ----
    // Lý do: AGENTS.md Rule 15 — code Community là public MIT và phải dùng được
    // thật (user phải xem được chart của mình). Fail-closed chỉ áp cho pro+.
    // Khớp `useTrademarkCompliance.ts:42` vốn đã coi "none" là tier thấp nhất.
    basic_chart: "none",
    common_indicators: "none",
    basic_drawing: "none",
    watchlist: "none",
    paper_market_limit: "none",
    data_window: "none",
    i18n: "none",
    theme: "none",
    // ---- PROMOTED TO COMMUNITY 2026-10-08 (COORD-2026-10-08) ----
    // These six carried a `pro` threshold with NO call site anywhere in either repo: the
    // name was in the matrix, in BACKEND_REQUIREMENT (a backend flag, not a tier) and in
    // upsell copy — and nowhere else. Sales copy existed, enforcement did not.
    //
    // They are lowered, not gated, because in all six cases gating would have REMOVED
    // working functionality from free users. That is a Rule 15 violation (Community code
    // is public MIT and must actually work), not a revenue gain:
    //   measure_pane / shortcut_map / volume_profile / volume_footprint / scale_sync run
    //     unconditionally today; measurement, shortcuts, volume-at-price and workspace
    //     scale sync are Community in every mainstream product.
    //   gap_compression is not even a feature: `ChartScaleAdapter.getChartScale` derives
    //     discontinuity from the DATA, has no toggle and no setting, and its only consumer
    //     is a dead `useCallback` (`VNInvestCharts.tsx:1301`, zero references). A gate there
    //     would enforce nothing AND leave free users with a broken scale.
    //
    // The upsell copy for all six was deleted in the same commit:
    // `src/widget/i18n/capability.messages.{vi,en}.ts`. `capabilityMessages.test.ts` fails
    // if a Community capability still has one, so this cannot drift back silently.
    measure_pane: "none",
    shortcut_map: "none",
    volume_profile: "none",
    volume_footprint: "none",
    scale_sync: "none",
    gap_compression: "none",
    // ---- three more, decided 2026-10-12 (same reasoning as the six above) ----
    // `indicator_histogram` / `indicator_advanced` / `session_hours` each carried a
    // `pro` threshold with no call site in either repo, while the thing they promise
    // is either already free or does not exist as a separable surface:
    //   - Histogram renders via the series registry (`priceChartTypes.ts:174`) with
    //     no `tier`, and the ATR/Stochastic/ADX/Aroon/ZigZag studies are computed
    //     free. Gating them would remove working indicators from free users (Rule 15),
    //     so the promise is withdrawn instead. `13.03` §2 records the same call for the
    //     six above, and §4 shows the sub-gate alternative was rejected for this shape.
    //   - `session_hours` had no tier check anywhere; the session-shading block is a
    //     rendering convention, which is exactly the `measure_pane` answer.
    // Copy for all three is deleted in the same commit and
    // `capabilityMessages.test.ts` fails if a Community capability regains one.
    indicator_histogram: "none",
    indicator_advanced: "none",
    session_hours: "none",
    // ---- PROMOTED TO COMMUNITY 2026-10-22 (DEV_PHASE_OFFICIAL_2026-10-22, quyết định D2) ----
    // `smart_drawing_pro` gated the 16 "TIER-16" drawing tools (gannBox, schiffPitchfork,
    // xabcdPattern, cypherPattern, elliott*, fibArc, fibCircles, fibSpeedResistance*,
    // fibSpiral). It is lowered, not gated, for ONE reason that no product argument
    // overrides: **the implementation is already public.**
    //
    // MEASURED 2026-10-22 on the published tarballs: `@vninvestcharts/engine-drawing@2.2.0`
    // shipped `dist/index.js` UNMINIFIED with 92 `// src/...` markers, and exported
    // `calculateFibArcGeometry`, `calculateGannAdvancedGeometry`,
    // `calculateElliottWaveGeometry`, `XabcdPattern`, `CypherPattern`, `FibSpiral`,
    // `GannBox`, `SchiffPitchfork` — plus the literal `const phi = 1.6180339887;`
    // (the FibSpiral geometry). The algorithms are, in the author's own words
    // (`REPORT_FINAL.md:545-558`), "toán sách vở": Fibonacci ratios, Gann eighths,
    // Schiff 50% offsets.
    //
    // So a Pro gate on them could not withhold anything a determined user did not
    // already have, and the ONLY effect it had was to remove 16 working tools from
    // free users — the exact Rule 15 violation the six (2026-10-08) and the three
    // (2026-10-12) above were demoted for. The tier gate is neutralized by THIS row:
    // `isPremiumDrawingBlocked()` consults `hasCapability(tier, "smart_drawing_pro")`
    // (`patTier.ts:81-84`), so minTier "none" opens the list at every tier, including
    // the unverified `none` tier.
    //
    // The upsell copy was deleted in the same change
    // (`src/widget/i18n/capability.messages.{en,vi}.ts`); `capabilityMessages.test.ts`
    // fails if a Community capability regains one, so this cannot drift back silently.
    // `PREMIUM_DRAWING_TYPES` (`patTier.ts:58-66`) is KEPT as the registry of the TIER-16
    // set — it is the regression lock, not a gate.
    smart_drawing_pro: "none",
    // ---- starter ----
    multi_chart: "starter",
    layout_preset: "starter",
    chart_compare: "starter",
    // ---- pro (Track A — client-side) ----
    dock_pro: "pro",
    no_ads: "pro",
    pro_plugin: "pro",
    indicator_full: "pro",
    chart_baseline: "pro",
    chart_exotic: "pro",
    pine_editor: "pro",
    pine_functional: "pro",
    strategy_tester: "pro",
    replay: "pro",
    replay_trading: "pro",
    paper_advanced: "pro",
    alert_local: "pro",
    alert_drawing: "pro",
    export_png: "pro",
    export_csv: "pro",
    auto_pattern: "pro",
    layout_user: "pro",
    share_local: "pro",
    // ---- enterprise (Track B — cần backend EP) ----
    whale_campaign: "enterprise",
    portfolio_analytics: "enterprise",
    adjusted_bars: "enterprise",
    cloud_alert: "enterprise",
    webhook: "enterprise",
    orderbook_dom: "enterprise",
    fundamentals_full: "enterprise",
    index_constituents: "enterprise",
    deep_history: "enterprise",
    server_pine: "enterprise",
    server_workspace: "enterprise",
    realtime_tick: "enterprise",
};
export const ALL_CAPABILITIES = Object.keys(MIN_TIER);
/** Tier tối thiểu cần có để mở capability — dùng cho UI upsell (`LockedFeature`). */
export function minTierFor(capability) {
    return MIN_TIER[capability];
}
/** Tier dùng khi chưa xác minh được gì. Coi như thấp nhất ⇒ chỉ Community. */
const UNKNOWN_TIER = "none";
/** Quyết định duy nhất. Mọi gate (UI, engine, upsell) phải gọi hàm này. */
export function hasCapability(tier, capability) {
    // `null`/`undefined` = CHƯA BIẾT. Áp xuống `none` (thấp nhất) thay vì "không cấp
    // gì": fail-closed cho mọi thứ bán được (pro+), nhưng Community vẫn mở — theo
    // AGENTS.md Rule 15, code Community là public MIT và user phải xem được chart
    // của chính mình. Widget cũng vốn default tier là "free" khi rỗng
    // (`usePatTierAuthentication.ts:234`) nên đường này là belt-and-braces.
    const effective = tier ?? UNKNOWN_TIER;
    return TIER_INDEX[effective] >= TIER_INDEX[MIN_TIER[capability]];
}
/** Danh sách capability mở cho tier — dùng cho badge, diff, và test đơn điệu. */
export function capabilitiesFor(tier) {
    if (!tier)
        return capabilitiesFor(UNKNOWN_TIER);
    return ALL_CAPABILITIES.filter((c) => hasCapability(tier, c));
}
/** Capability mới có (chỉ dùng cho diff khi nâng tier, không dùng để gate). */
export function capabilitiesDelta(from, to) {
    const before = new Set(capabilitiesFor(from));
    return capabilitiesFor(to).filter((c) => !before.has(c));
}
/**
 * Ads: thay cho `requiresAds`/`FREE_AD_TIERS` cũ.
 * `free` + `starter` thấy quảng cáo; `none` (chưa xác minh) CŨNG thấy — fail-closed.
 */
export function tierRequiresAds(tier) {
    return !hasCapability(tier, "no_ads");
}
